import React, { useEffect, useRef, useState } from "react";
import "./MapSection.css";
import loadGoogleMaps from "../utils/loadGoogleMaps";
import { GOOGLE_MAPS_API_KEY, MAP_DEFAULTS } from "../config";
import locations from "../data/locations.json";
import { resolveAsset, pinNormal, pinHighlighted } from "../data/assets";

function escapeHtml(value) {
  return String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c]
  );
}

export default function MapSection() {
  const mapRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const pinned = locations.filter(
      (l) => typeof l.lat === "number" && typeof l.lng === "number"
    );

    if (pinned.length === 0) {
      setStatus("empty");
      return;
    }

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then((maps) => {
        if (cancelled || !mapRef.current) return;
        setStatus("ready");

        const map = new maps.Map(mapRef.current, {
          center: MAP_DEFAULTS.center,
          zoom: MAP_DEFAULTS.zoom,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: "cooperative",
        });

        const canHover =
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(hover: hover)").matches;

        class Popup extends maps.OverlayView {
          constructor() {
            super();
            this.position = null;
            this.hideTimer = null;
            this.visible = false;
            this.activeId = null;
            this.el = document.createElement("div");
            this.el.className = "beef-map-popup";
            this.el.style.position = "absolute";
            this.el.style.display = "none";
            this.el.addEventListener("click", (e) => e.stopPropagation());
            if (canHover) {
              this.el.addEventListener("mouseenter", () => this.cancelHide());
              this.el.addEventListener("mouseleave", () => this.scheduleHide());
            }
          }
          setContent(loc) {
            const thumb = resolveAsset(loc.thumbnail);
            const meta = [
              escapeHtml(loc.address),
              loc.hours ? escapeHtml(loc.hours) : "",
            ]
              .filter(Boolean)
              .join("<br/>");
            const cta =
              loc.cta && loc.cta.href
                ? `<a class="beef-btn beef-btn--sm" href="${escapeHtml(
                    loc.cta.href
                  )}" target="_blank" rel="noopener noreferrer">${escapeHtml(
                    loc.cta.label || "Learn More"
                  )}</a>`
                : "";
            this.el.innerHTML =
              `<div class="beef-map-popup__inner">` +
              `<div class="beef-map-popup__body">` +
              `<div class="beef-map-popup__text">` +
              `<p class="beef-map-popup__title">${escapeHtml(loc.partner)}</p>` +
              `<p class="beef-map-popup__meta">${meta}</p>` +
              `</div>${cta}</div>` +
              (thumb
                ? `<div class="beef-map-popup__media"><img src="${thumb}" alt="" loading="lazy" decoding="async" /></div>`
                : "") +
              `<span class="beef-map-popup__tail" aria-hidden="true"></span>` +
              `</div>`;
          }
          setPosition(latLng) {
            this.position = latLng;
          }
          cancelHide() {
            if (this.hideTimer) {
              clearTimeout(this.hideTimer);
              this.hideTimer = null;
            }
          }
          scheduleHide() {
            this.cancelHide();
            this.hideTimer = setTimeout(() => this.hide(), 150);
          }
          hide() {
            this.cancelHide();
            this.visible = false;
            this.activeId = null;
            this.el.style.display = "none";
          }
          show(loc, latLng) {
            this.cancelHide();
            this.visible = true;
            this.activeId = loc.id;
            this.setContent(loc);
            this.setPosition(latLng);
            this.el.style.display = "block";
            this.draw();
          }
          onAdd() {
            const panes = this.getPanes();
            if (panes) panes.floatPane.appendChild(this.el);
          }
          onRemove() {
            if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
          }
          draw() {
            if (!this.position) return;
            const projection = this.getProjection();
            if (!projection) return;
            const divPoint = projection.fromLatLngToDivPixel(this.position);
            if (divPoint) {
              this.el.style.left = divPoint.x + "px";
              this.el.style.top = divPoint.y + "px";
            }
            if (!canHover) return;
            const containerPoint = projection.fromLatLngToContainerPixel(
              this.position
            );
            if (containerPoint) {
              this.el.classList.toggle(
                "beef-map-popup--below",
                containerPoint.y < this.el.offsetHeight + 48
              );
              const mapDiv = this.getMap() && this.getMap().getDiv();
              const mapWidth = mapDiv ? mapDiv.offsetWidth : 0;
              this.el.classList.toggle(
                "beef-map-popup--left",
                containerPoint.x - 24 + this.el.offsetWidth > mapWidth - 8
              );
            }
          }
          centerCard() {
            const map = this.getMap();
            if (!map || !this.el) return;
            const mapRect = map.getDiv().getBoundingClientRect();
            const cardRect = this.el.getBoundingClientRect();
            if (!cardRect.width || !cardRect.height) return;
            const cardCenterX =
              cardRect.left + cardRect.width / 2 - mapRect.left;
            const cardCenterY =
              cardRect.top + cardRect.height / 2 - mapRect.top;
            map.panBy(
              cardCenterX - mapRect.width / 2,
              cardCenterY - mapRect.height / 2
            );
          }
        }

        const popup = new Popup();
        popup.setMap(map);

        if (!canHover) {
          map.addListener("click", () => popup.hide());
        }

        const bounds = new maps.LatLngBounds();
        pinned.forEach((loc) => {
          const position = { lat: loc.lat, lng: loc.lng };
          bounds.extend(position);
          const marker = new maps.Marker({
            position,
            map,
            title: loc.partner || "",
            icon: {
              url: loc.highlighted ? pinHighlighted : pinNormal,
              scaledSize: new maps.Size(28, 34),
              anchor: new maps.Point(14, 34),
            },
          });
          const latLng = new maps.LatLng(loc.lat, loc.lng);
          if (canHover) {
            marker.addListener("mouseover", () => popup.show(loc, latLng));
            marker.addListener("mouseout", () => popup.scheduleHide());
            marker.addListener("click", () => popup.show(loc, latLng));
          } else {
            marker.addListener("click", () => {
              if (popup.visible && popup.activeId === loc.id) {
                popup.hide();
              } else {
                popup.show(loc, latLng);
                window.requestAnimationFrame(() => popup.centerCard());
              }
            });
          }
        });

        if (pinned.length === 1) {
          map.setCenter(pinned[0]);
          map.setZoom(14);
        } else {
          map.fitBounds(bounds, 80);
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="beef-map" className="beef-map">
      <div className="beef-map__canvas" ref={mapRef} />
      {status === "error" ? (
        <div className="beef-map__notice">Map is temporarily unavailable.</div>
      ) : null}
      {status === "empty" ? (
        <div className="beef-map__notice">
          Participating locations coming soon.
        </div>
      ) : null}
    </section>
  );
}
